import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import { attachEvidenceFromLibrary, fetchEvidenceList, linkEvidence, uploadEvidence } from './api';
import type {
  AttachEvidenceFromLibraryInput,
  EvidenceFileDto,
  EvidenceType,
  LinkRequestInput,
  TargetRecordType,
} from './types';

const KEY = 'evidence' as const;

export const useEvidenceList = (
  batchId: string | undefined,
  filter?: { evidence_type?: EvidenceType; target_record_type?: TargetRecordType },
) => {
  return useQuery<EvidenceFileDto[], ApiClientError>({
    queryKey: [KEY, 'list', batchId, filter],
    queryFn: () => fetchEvidenceList(batchId!, filter),
    enabled: !!batchId,
  });
};

export const useUploadEvidence = () => {
  const qc = useQueryClient();
  return useMutation<
    EvidenceFileDto,
    ApiClientError,
    { batchId: string; file: File; evidence_type: EvidenceType }
  >({
    mutationFn: ({ batchId, file, evidence_type }) => uploadEvidence(batchId, file, evidence_type),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [KEY, 'list', vars.batchId] });
    },
  });
};

export const useAttachEvidenceFromLibrary = () => {
  const qc = useQueryClient();
  return useMutation<EvidenceFileDto, ApiClientError, AttachEvidenceFromLibraryInput>({
    mutationFn: attachEvidenceFromLibrary,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [KEY, 'list', vars.batchId] });
    },
  });
};

export const useLinkEvidence = () => {
  const qc = useQueryClient();
  return useMutation<
    EvidenceFileDto,
    ApiClientError,
    { evidenceFileId: string; body: LinkRequestInput }
  >({
    mutationFn: ({ evidenceFileId, body }) => linkEvidence(evidenceFileId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
};
